import { Popover, Table, Tag, Tooltip } from 'antd';
import { InboxOutlined, UserOutlined } from '@ant-design/icons';
import React, { useMemo } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DAY_FORMAT, DAYTIME_FORMAT, ORDER_STATUSES } from '../../constants';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import useColumnConfig from '../../hooks/useColumnConfig';
import ColumnConfigDrawer from '../ColumnConfigDrawer';
import PartnerLogo from '../PartnerLogo';
import { tenantConfig } from '../../config/tenantConfig';

const MOBILE_HIDDEN_KEYS = [
  'pickupName',
  'pickupAddress',
  'deliveryAddress',
  'pickupTimeWindow',
  'deliveryTimeWindow',
  'volume',
  'partner',
  'deliveryPhone',
  'collectedItems',
  'weight',
  'productTypes',
  'scheduleId',
];

const FIXED_KEYS = ['orderCode', 'status', 'action'];

// OVO-101 — shared renderer for pickup/delivery Site cells. Returns a
// clickable blue Tag → /admin/partners/:partnerId/sites?focusSiteId=N
// (PartnerSites opens the editor modal on mount). Falls back to a plain
// Tag for partner viewers (no admin Sites access), or "—" when the FK
// is null. partnerId is read off the order row so the link points at
// the correct partner even on the global Orders page.
function renderSiteCell(site, partnerId, linkPrefix) {
  if (!site) return <span style={{ color: '#bbb' }}>—</span>;
  const label = (
    <Tag color="blue" style={{ fontSize: 11, marginInlineEnd: 0 }}>
      {site.name}
    </Tag>
  );
  const isPartnerView = linkPrefix?.startsWith('/partner');
  if (isPartnerView || !partnerId) return label;
  return (
    <Link to={`/admin/partners/${partnerId}/sites?focusSiteId=${site.id}`} title={`Open Site "${site.name}"`}>
      {label}
    </Link>
  );
}

// OVO-206 — render the linked collection box ("Caisse") as a tag. Shows the
// box number when present (CT's "7752"), else the name. Links to the box
// page for operators; stays read-only for partner viewers (no admin routes).
function renderCollectionPointCell(cp, linkable) {
  if (!cp) return <span style={{ color: '#bbb' }}>—</span>;
  const txt = cp.externalRef ? `${cp.externalRef} · ${cp.name}` : cp.name;
  const tag = (
    <Tag
      color="geekblue"
      style={{ fontSize: 11, marginInlineEnd: 0, cursor: linkable ? 'pointer' : undefined }}
      title={txt}
    >
      {txt}
    </Tag>
  );
  if (!linkable) return tag;
  return (
    <Link to={`/admin/collection-points/${cp.id}`} onClick={(e) => e.stopPropagation()}>
      {tag}
    </Link>
  );
}

export default function OrdersTable({
  withAction,
  dataSource,
  selectedRowKeys,
  onSelectChange,
  configRef,
  onPageSizeChange: onPageSizeChangeProp,
  linkPrefix = '/admin/orders',
  partnerDefaults,
  ...props
}) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const { t } = useTranslation('orders');
  const { t: tc } = useTranslation('common');

  const allColumns = useMemo(() => {
    const cols = [
      {
        title: t('orderId'),
        dataIndex: 'orderCode',
        key: 'orderCode',
        fixed: 'left',
        width: 200,
        className: 'border-right',
        render: (value) => (
          <Link to={`${linkPrefix}/${value}`} style={{ color: 'var(--accent-strong)' }}>
            {value}
          </Link>
        ),
      },
      {
        title: <Tooltip title={t('orderRefTooltip')}>{t('orderRef')}</Tooltip>,
        dataIndex: 'orderRef',
        key: 'orderRef',
        width: 200,
        ellipsis: true,
      },
      {
        // Materialised by a recurring schedule (OVO-113). The order ref is
        // always SCH-<scheduleId>-<date>, but that's easy to miss, so the
        // link is explicit and jumps to the partner's schedules.
        title: t('recurring'),
        dataIndex: 'scheduleId',
        key: 'scheduleId',
        render: (scheduleId, record) =>
          scheduleId ? (
            <Link
              to={`/admin/recurring?partnerId=${record.partnerRef?.id ?? record.partnerId ?? ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Tag color="purple" style={{ cursor: 'pointer' }}>
                #{scheduleId}
              </Tag>
            </Link>
          ) : (
            ''
          ),
      },
      {
        title: <Tooltip title={t('partnerOrderRefTooltip')}>{t('partnerOrderRef')}</Tooltip>,
        dataIndex: 'partnerOrderRef',
        key: 'partnerOrderRef',
        width: 200,
        ellipsis: true,
      },
      {
        title: t('partner'),
        dataIndex: 'partner',
        key: 'partner',
        width: 200,
        render: (value, record) => {
          const label = value || record?.partnerRef?.name || '';
          if (!label) return '';
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <PartnerLogo name={record?.partnerRef?.name || label} logoKey={record?.partnerRef?.logoFile?.key} />
              <span>{label}</span>
            </span>
          );
        },
      },
      {
        title: t('deliveryDate'),
        dataIndex: 'deliveryDate',
        key: 'deliveryDate',
        sorter: true,
        render: (value) => {
          return moment(value).format(DAY_FORMAT);
        },
      },
      {
        title: t('deliveryName'),
        dataIndex: 'name',
        key: 'name',
        width: 200,
      },
      {
        title: t('deliveryAddress'),
        dataIndex: 'deliveryAddress',
        key: 'deliveryAddress',
        width: 300,
        ellipsis: true,
      },
      {
        title: t('routeId'),
        dataIndex: 'routeId',
        key: 'routeId',
        width: 200,
        render: (value) => {
          if (!value) return '';
          // OVO-002 — partner viewers (linkPrefix='/partner/orders') must
          // land on the partner-scoped route detail, not the admin one.
          const routesBase = linkPrefix.startsWith('/partner') ? '/partner/routes' : '/admin/routes';
          return (
            <Link to={`${routesBase}/${value}`} onClick={(e) => e.stopPropagation()}>
              {value}
            </Link>
          );
        },
      },
      {
        title: (
          <Tooltip title={t('typesTooltip')}>
            <span style={{ borderBottom: '1px dashed #999', cursor: 'help' }}>{t('types')}</span>
          </Tooltip>
        ),
        dataIndex: 'types',
        key: 'types',
        width: 200,
        render: (value) => {
          if (!value || !value.length) return '-';
          return value.map((type) => (
            <Tag key={type} style={{ marginBottom: 2 }}>
              {type}
            </Tag>
          ));
        },
      },
      {
        title: tc('createdAt'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 250,
        render: (value) => {
          return value ? moment(value).format(DAYTIME_FORMAT) : '';
        },
      },
      {
        title: t('pickupName'),
        dataIndex: 'pickupName',
        key: 'pickupName',
        width: 200,
        ellipsis: true,
      },
      {
        title: t('pickupAddress'),
        dataIndex: 'pickupAddress',
        key: 'pickupAddress',
        width: 300,
        ellipsis: true,
      },
      // OVO-101 — resolved Sites split per side. Each column shows the
      // FK-linked Site name as a clickable Tag → navigates to
      // `/admin/partners/:partnerId/sites?focusSiteId=N` (PartnerSites
      // opens the editor for the focused site on mount). For partner_viewer
      // (linkPrefix='/partner') the tag is non-clickable since the admin
      // Sites tab is admin-only. The siteId filter still OR-matches
      // pickup vs delivery — one filter, two visualization columns.
      {
        title: t('pickupSite'),
        key: 'pickupSite',
        width: 180,
        ellipsis: true,
        render: (_, record) => renderSiteCell(record.pickupSite, record.partnerId, linkPrefix),
      },
      {
        title: t('deliverySite'),
        key: 'deliverySite',
        width: 180,
        ellipsis: true,
        render: (_, record) => renderSiteCell(record.deliverySite, record.partnerId, linkPrefix),
      },
      {
        // OVO-206 — the collection box this order serviced.
        title: t('collectionPoint'),
        key: 'collectionPoint',
        width: 170,
        ellipsis: true,
        render: (_, record) => renderCollectionPointCell(record.collectionPoint, !linkPrefix?.startsWith('/partner')),
      },
      {
        title: t('deliveryPhone'),
        dataIndex: 'deliveryPhone',
        key: 'deliveryPhone',
        width: 200,
      },
      {
        title: t('pickupTimeWindow'),
        dataIndex: 'pickupTimeWindow',
        key: 'pickupTimeWindow',
        width: 200,
        ellipsis: true,
        render(_value, record) {
          return (record.pickupTimeStart || '') + ' - ' + (record.pickupTimeEnd || '');
        },
      },
      {
        title: t('deliveryTimeWindow'),
        dataIndex: 'deliveryTimeWindow',
        key: 'deliveryTimeWindow',
        width: 200,
        ellipsis: true,
        render(_value, record) {
          return (record.deliveryTimeStart || '') + ' - ' + (record.deliveryTimeEnd || '');
        },
      },
      {
        title: tc('volume'),
        dataIndex: 'volume',
        key: 'volume',
        width: 200,
      },
      {
        title: tc('updatedAt'),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 250,
        render: (value) => {
          return value ? moment(value).format(DAYTIME_FORMAT) : '';
        },
      },
      {
        // OVO-019 — counts EMPTY-category CollectedItems only (returnables
        // dropped at the hub). PRODUCT-category items (pickup-weight
        // enrichment) are now surfaced via the Weight column and are NOT
        // counted here. Internal `key` stays 'collectedItems' to preserve
        // users' saved column order in localStorage.
        title: t('empties'),
        key: 'collectedItems',
        width: 90,
        align: 'center',
        render: (_, record) => {
          const count = record._count?.collectedItems || 0;
          return count > 0 ? (
            <Tag icon={<InboxOutlined />} color="orange">
              {count}
            </Tag>
          ) : (
            '-'
          );
        },
      },
      {
        // OVO-019 — comma-joined list of product types (each OrderProduct's
        // PartnerItemType name). Empty for orders with no products.
        title: t('productType'),
        key: 'productTypes',
        width: 200,
        render: (_, record) => {
          const products = record.orderProducts || [];
          if (products.length === 0) return '-';
          const names = products.map((p) => p.partnerItemType?.name).filter(Boolean);
          return names.length > 0 ? names.join(', ') : '-';
        },
      },
      {
        // OVO-019 — effective weight per OrderProduct.
        //
        // Source-of-truth rules:
        //   - OrderProduct.weight != null            → dispatcher prefilled
        //   - OrderProduct.weight == null AND a
        //     matching PRODUCT-category CollectedItem
        //     exists with weight                     → driver captured at pickup
        //   - neither, and PartnerItemType.requiresWeight=true → pending
        //
        // Visual: dispatcher values render plain (the typical case, no
        // clutter). Driver-captured values get a small green user icon
        // suffix with a tooltip carrying the driver name + timestamp.
        // When both exist with different values, the dispatcher's value
        // wins on screen with a tooltip surfacing the driver's value.
        title: t('weight'),
        key: 'weight',
        width: 200,
        render: (_, record) => {
          const products = record.orderProducts || [];
          if (products.length === 0) return '-';
          const captured = record.collectedItems || [];
          const cells = products.map((p) => {
            const ci = captured.find((c) => c.partnerItemTypeId === p.partnerItemType?.id);
            const driverWeight = ci?.weight;
            const dispatcherWeight = p.weight;
            const unit = p.partnerItemType?.unitOfMeasure || 'kg';

            // Driver badge — a small green user icon + tooltip.
            const driverBadge = ci ? (
              <Tooltip
                title={
                  ci.collectedByUser?.name
                    ? `Captured by ${ci.collectedByUser.name}${
                        ci.collectedAt ? ` · ${moment(ci.collectedAt).format(DAYTIME_FORMAT)}` : ''
                      }`
                    : 'Captured by driver'
                }
              >
                <UserOutlined style={{ color: '#52c41a', marginLeft: 4 }} />
              </Tooltip>
            ) : null;

            // Both filled with different values → dispatcher on screen,
            // tooltip surfaces the driver capture (no green icon — the
            // dispatcher value is the authoritative one shown).
            if (dispatcherWeight != null && driverWeight != null && driverWeight !== dispatcherWeight) {
              return (
                <Tooltip
                  title={`Driver captured: ${driverWeight} ${unit}${
                    ci?.collectedByUser?.name ? ` (${ci.collectedByUser.name})` : ''
                  }`}
                  key={p.id}
                >
                  <span>{`${dispatcherWeight} ${unit}`}</span>
                </Tooltip>
              );
            }
            if (dispatcherWeight != null) {
              return <span key={p.id}>{`${dispatcherWeight} ${unit}`}</span>;
            }
            if (driverWeight != null) {
              return (
                <span key={p.id}>
                  {`${driverWeight} ${unit}`}
                  {driverBadge}
                </span>
              );
            }
            // Pending capture — dispatcher left blank, driver hasn't filled
            if (p.partnerItemType?.requiresWeight) {
              return (
                <Tag color="orange" key={p.id}>
                  pending
                </Tag>
              );
            }
            return <span key={p.id}>-</span>;
          });
          // Comma-join the rendered cells.
          return cells.map((cell, i) => (
            <React.Fragment key={i}>
              {i > 0 && ', '}
              {cell}
            </React.Fragment>
          ));
        },
      },
      {
        title: t('scanned'),
        dataIndex: 'scannedCode',
        key: 'scannedCode',
        width: 120,
        render: (value, record) => {
          if (!value) {
            return <span className="scan-status not-scanned">-</span>;
          }
          return (
            <Popover
              title={t('scannedCode')}
              content={
                <div className="scanned-code-popover">
                  <div className="code">{value}</div>
                  {record.scannedCodeType && <div className="type">Type: {record.scannedCodeType}</div>}
                  {record.scannedAt && (
                    <div className="time">Scanned: {moment(record.scannedAt).format(DAYTIME_FORMAT)}</div>
                  )}
                </div>
              }
            >
              <span className="scan-status scanned">Scanned</span>
            </Popover>
          );
        },
      },
      {
        title: tc('status'),
        dataIndex: 'status',
        key: 'status',
        fixed: 'right',
        width: 200,
        className: 'border-left',
        render: (value, record) => {
          return value === ORDER_STATUSES.ERROR && record.errorMessages?.length > 0 ? (
            <Popover
              title={t('errorDetails')}
              content={
                <div className="popupErrorMessages">
                  {record.errorMessages.map((error, index) => {
                    return (
                      <div key={index} className="item">
                        {error}
                      </div>
                    );
                  })}
                </div>
              }
            >
              <span className={'status order ' + value.toLowerCase()}>{tc(`orderStatuses.${value}`)}</span>
            </Popover>
          ) : (
            <span className={'status order ' + value.toLowerCase()}>{tc(`orderStatuses.${value}`)}</span>
          );
        },
      },
    ];

    if (withAction) {
      cols.push({
        key: 'action',
        width: 120,
        fixed: 'right',
        render(record) {
          return withAction(record);
        },
      });
    }

    // OVO-132 — drop the Partner column in a single-tenant instance.
    return tenantConfig.isSingleTenant ? cols.filter((c) => c.key !== 'partner') : cols;
  }, [withAction]);

  const columnConfig = useColumnConfig('orders-columns', allColumns, FIXED_KEYS, partnerDefaults);

  // Expose openDrawer to parent via ref
  if (configRef) {
    configRef.current = { openDrawer: columnConfig.openDrawer, pageSize: columnConfig.pageSize };
  }

  const displayColumns = isMobile
    ? columnConfig.visibleColumns.filter((col) => !MOBILE_HIDDEN_KEYS.includes(col.key || col.dataIndex))
    : columnConfig.visibleColumns;

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <div className="table-orders table-common">
      <Table
        columns={displayColumns}
        rowSelection={rowSelection}
        dataSource={dataSource}
        scroll={{ x: 'max-content' }}
        // bordered
        {...props}
      />
      <ColumnConfigDrawer
        open={columnConfig.drawerOpen}
        onClose={columnConfig.closeDrawer}
        columns={columnConfig.orderedAllColumns}
        hidden={columnConfig.hidden}
        fixedKeys={FIXED_KEYS}
        onToggle={columnConfig.toggleColumn}
        onMove={columnConfig.moveColumn}
        onReset={columnConfig.resetConfig}
        pageSize={columnConfig.pageSize}
        onPageSizeChange={(size) => {
          columnConfig.setPageSize(size);
          onPageSizeChangeProp?.(size);
        }}
      />
    </div>
  );
}
